import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Compass, IndianRupee, TrendingUp, AlertTriangle, MapPin, Building2,
  Wallet, ShieldAlert, Activity, ArrowDownToLine
} from 'lucide-react';

const healthColor = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
  unknown: 'bg-slate-400',
};
const healthLabel = {
  green: 'Strong',
  yellow: 'Stable',
  red: 'At Risk',
  unknown: 'Insufficient Data',
};
const healthBg = {
  green: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200',
  yellow: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200',
  red: 'bg-red-50 dark:bg-red-950/20 border-red-200',
  unknown: 'bg-slate-50 dark:bg-slate-900/40 border-slate-200',
};
const healthText = {
  green: 'text-emerald-700',
  yellow: 'text-amber-700',
  red: 'text-red-700',
  unknown: 'text-slate-600',
};

const formatINR = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function AdminPortfolio() {
  const [runway, setRunway] = useState(null);
  const [geo, setGeo] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/super-admin/portfolio/runway'),
      api.get('/super-admin/portfolio/geo-heatmap').catch(() => null),
      api.get('/super-admin/portfolio/regional-alerts?threshold=2').catch(() => null),
      api.get('/super-admin/razorpay/payouts').catch(() => null),
    ]).then(([rw, hm, al, po]) => {
      setRunway(rw.data);
      if (hm) setGeo(hm.data);
      if (al) setAlerts(al.data);
      if (po) setPayouts(po.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const exportPortfolioCsv = () => {
    if (!runway?.per_academy) return;
    const headers = ['Academy', 'City', 'State', 'Status', 'Health', 'Confidence', 'Monthly Income', 'Monthly Burn', '90D Cash Position', 'Collection Rate', 'Overdue Amount', 'Overdue Count'];
    const rows = runway.per_academy.map(a => [
      a.name, a.city || '', a.state || '', a.subscription_status || '', a.health, a.confidence,
      a.monthly_income, a.monthly_burn, a.cash_position_90d, a.current_collection_rate, a.overdue_amount, a.overdue_count,
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Portfolio_Forecast_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="admin-portfolio-loading">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!runway) {
    return <div className="text-center py-16 text-muted-foreground" data-testid="admin-portfolio-empty">No portfolio data available yet.</div>;
  }

  const t = runway.platform_runway || {};
  const mrr = runway.mrr || {};
  const totalAcademies = runway.total_academies || 0;
  const redPct = totalAcademies ? Math.round((t.academies_red / totalAcademies) * 100) : 0;
  const platformCashPos = t.cash_position_90d || 0;

  return (
    <div className="space-y-6" data-testid="admin-portfolio">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Compass className="h-6 w-6 text-primary" /> Global Portfolio Forecast
          </h1>
          <p className="text-sm text-muted-foreground">Platform-wide cash runway, churn risk & geographical profitability</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportPortfolioCsv} data-testid="portfolio-export-csv">
            <ArrowDownToLine className="h-4 w-4 mr-1" /> Export CSV
          </Button>
          <Badge variant="outline" className="font-normal text-[10px]">
            {totalAcademies} academies · refreshed {new Date(runway.generated_at).toLocaleTimeString()}
          </Badge>
        </div>
      </div>

      {/* ===== Hero — Platform 90-Day Cash Position ===== */}
      <Card className="overflow-hidden" data-testid="portfolio-hero">
        <CardContent className="p-0">
          <div className={`p-6 text-white ${
            platformCashPos > 0 ? 'bg-gradient-to-br from-indigo-700 via-violet-700 to-purple-800'
            : 'bg-gradient-to-br from-red-700 via-rose-800 to-rose-900'
          }`}>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider opacity-80 flex items-center gap-1.5">
                  <Wallet className="h-3.5 w-3.5" /> Platform 90-Day Position
                </p>
                <p className="text-4xl font-display font-bold tabular-nums mt-2" data-testid="portfolio-cash-position">
                  {platformCashPos >= 0 ? '+' : '-'}{formatINR(Math.abs(platformCashPos))}
                </p>
                <p className="text-xs opacity-80 mt-1">
                  Income {formatINR(t.projected_income_90d)} − Burn {formatINR(t.projected_burn_90d)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider opacity-80 flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" /> MRR (last 30d)
                </p>
                <p className="text-4xl font-display font-bold tabular-nums mt-2" data-testid="portfolio-mrr">
                  {formatINR(mrr.last_30_days_inr)}
                </p>
                <p className="text-xs opacity-80 mt-1">{mrr.invoice_count || 0} paid platform invoices</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider opacity-80 flex items-center gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5" /> At-Risk Academies
                </p>
                <p className="text-4xl font-display font-bold tabular-nums mt-2" data-testid="portfolio-red-count">
                  {t.academies_red || 0}
                  <span className="text-xl opacity-70 ml-1">/{totalAcademies}</span>
                </p>
                <p className="text-xs opacity-80 mt-1">{redPct}% of portfolio in red — churn risk</p>
              </div>
            </div>
          </div>

          {/* Health distribution strip */}
          <div className="p-4 bg-card border-t">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Portfolio Health Distribution</p>
            <div className="flex h-8 rounded-lg overflow-hidden border bg-muted">
              {['green', 'yellow', 'red', 'unknown'].map(h => {
                const count = t[`academies_${h}`] || 0;
                if (count === 0) return null;
                const pct = (count / totalAcademies) * 100;
                return (
                  <div
                    key={h}
                    className={`${healthColor[h]} flex items-center justify-center text-white text-xs font-semibold`}
                    style={{ width: `${pct}%`, minWidth: '40px' }}
                    title={`${count} ${healthLabel[h]}`}
                    data-testid={`portfolio-health-${h}`}
                  >
                    {count}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-[11px] text-muted-foreground">
              {['green', 'yellow', 'red', 'unknown'].map(h => (
                <span key={h} className="flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-full ${healthColor[h]}`} />
                  {healthLabel[h]} · {t[`academies_${h}`] || 0}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Regional Alerts (P0 churn risk) ===== */}
      {alerts && alerts.alert_count > 0 && (
        <Card className="border-red-200 dark:border-red-900" data-testid="portfolio-alerts">
          <CardHeader className="pb-2 bg-red-50 dark:bg-red-950/20">
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4" /> Regional Churn Alerts ({alerts.alert_count})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 space-y-2">
            {alerts.alerts.map((a, i) => (
              <div key={i} className="p-3 rounded-lg border border-red-200 bg-red-50/50 dark:bg-red-950/10 flex items-start gap-3" data-testid={`portfolio-alert-${i}`}>
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    <Badge variant="outline" className="mr-2 capitalize text-[10px]">{a.region_type}</Badge>
                    {a.region}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{a.message}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ===== Tabs: Academies / Cities / States ===== */}
      <Tabs defaultValue="academies" className="space-y-4">
        <TabsList data-testid="portfolio-tabs">
          <TabsTrigger value="academies" data-testid="tab-academies">
            <Building2 className="h-3.5 w-3.5 mr-1" /> Academies
          </TabsTrigger>
          <TabsTrigger value="cities" data-testid="tab-cities">
            <MapPin className="h-3.5 w-3.5 mr-1" /> Cities
          </TabsTrigger>
          <TabsTrigger value="states" data-testid="tab-states">
            <MapPin className="h-3.5 w-3.5 mr-1" /> States
          </TabsTrigger>
          {payouts && (
            <TabsTrigger value="payouts" data-testid="tab-payouts">
              <IndianRupee className="h-3.5 w-3.5 mr-1" /> Razorpay Payouts
            </TabsTrigger>
          )}
        </TabsList>

        {/* ----- Academies (worst first) ----- */}
        <TabsContent value="academies">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" /> Academies (sorted by 90-day cash position — worst first)
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="portfolio-academies-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 pr-3 font-medium">Academy</th>
                    <th className="text-left py-2 pr-3 font-medium">Region</th>
                    <th className="text-center py-2 px-2 font-medium">Health</th>
                    <th className="text-right py-2 px-2 font-medium">Income/mo</th>
                    <th className="text-right py-2 px-2 font-medium">Burn/mo</th>
                    <th className="text-right py-2 px-2 font-medium">90D Cash</th>
                    <th className="text-right py-2 px-2 font-medium">Collection</th>
                    <th className="text-right py-2 pl-2 font-medium">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {(runway.per_academy || []).map((a) => (
                    <tr key={a.academy_id} className="border-b last:border-b-0 hover:bg-muted/40" data-testid={`portfolio-row-${a.academy_id}`}>
                      <td className="py-2 pr-3">
                        <p className="font-medium truncate max-w-[200px]">{a.name}</p>
                        <p className="text-[10px] text-muted-foreground">{a.custom_id || a.academy_id.slice(0, 8)}</p>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {a.city || '—'}{a.state ? `, ${a.state}` : ''}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${healthBg[a.health]} ${healthText[a.health]}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${healthColor[a.health]}`} />
                          <span className="text-[10px] uppercase font-semibold">{healthLabel[a.health]}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatINR(a.monthly_income)}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{formatINR(a.monthly_burn)}</td>
                      <td className={`py-2 px-2 text-right tabular-nums font-semibold ${a.cash_position_90d >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {a.cash_position_90d >= 0 ? '+' : '-'}{formatINR(Math.abs(a.cash_position_90d))}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{a.current_collection_rate}%</td>
                      <td className="py-2 pl-2 text-right tabular-nums text-red-700">{a.overdue_count > 0 ? formatINR(a.overdue_amount) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(runway.per_academy || []).length === 0 && (
                <p className="text-center text-xs text-muted-foreground py-6">No academies onboarded yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ----- Cities ----- */}
        <TabsContent value="cities">
          <GeoHeatmapTable rows={geo?.by_city || []} label="city" />
        </TabsContent>

        {/* ----- States ----- */}
        <TabsContent value="states">
          <GeoHeatmapTable rows={geo?.by_state || []} label="state" />
        </TabsContent>

        {/* ----- Razorpay Payouts ----- */}
        {payouts && (
          <TabsContent value="payouts">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Razorpay Settlements (T+5 heuristic)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portfolio-payouts">
                  <PayoutTile label="Settled" amount={payouts.settled?.total} count={payouts.settled?.count} tone="emerald" />
                  <PayoutTile label="Unsettled (T+5)" amount={payouts.unsettled?.total} count={payouts.unsettled?.count} tone="amber" />
                  <PayoutTile label="Last 30 Days" amount={payouts.last_30_days?.total} count={payouts.last_30_days?.count} tone="indigo" />
                  <PayoutTile label="Pending Collection" amount={payouts.pending_collection?.total} count={payouts.pending_collection?.count} tone="red" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">{payouts.unsettled?.note}</p>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function GeoHeatmapTable({ rows, label }) {
  if (!rows.length) {
    return <Card><CardContent className="text-center py-8 text-xs text-muted-foreground">No {label}-level data yet.</CardContent></Card>;
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" /> Profitability by {label.charAt(0).toUpperCase() + label.slice(1)}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`portfolio-${label}-table`}>
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-2 pr-3 font-medium capitalize">{label}</th>
              <th className="text-center py-2 px-2 font-medium">Academies</th>
              <th className="text-right py-2 px-2 font-medium">Monthly Income</th>
              <th className="text-right py-2 px-2 font-medium">Overdue</th>
              <th className="text-right py-2 px-2 font-medium">Avg Collection</th>
              <th className="text-left py-2 pl-3 font-medium">Health Mix</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.name} className="border-b last:border-b-0 hover:bg-muted/40" data-testid={`portfolio-${label}-row-${r.name}`}>
                <td className="py-2 pr-3 font-medium">{r.name}</td>
                <td className="py-2 px-2 text-center tabular-nums">{r.academy_count}</td>
                <td className="py-2 px-2 text-right tabular-nums">{formatINR(r.total_income_monthly)}</td>
                <td className="py-2 px-2 text-right tabular-nums text-red-700">{r.total_overdue > 0 ? formatINR(r.total_overdue) : '—'}</td>
                <td className="py-2 px-2 text-right tabular-nums">{r.avg_collection_rate}%</td>
                <td className="py-2 pl-3">
                  <div className="flex h-4 rounded overflow-hidden border bg-muted" style={{ width: '160px' }}>
                    {['green', 'yellow', 'red', 'unknown'].map(h => {
                      const c = r[`health_${h}`] || 0;
                      if (!c) return null;
                      const pct = (c / r.academy_count) * 100;
                      return <div key={h} className={healthColor[h]} style={{ width: `${pct}%` }} title={`${c} ${healthLabel[h]}`} />;
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function PayoutTile({ label, amount, count, tone }) {
  const toneMap = {
    emerald: 'from-emerald-500 to-emerald-700',
    amber: 'from-amber-500 to-orange-600',
    indigo: 'from-indigo-500 to-blue-700',
    red: 'from-red-500 to-rose-700',
  };
  return (
    <div className={`rounded-lg p-4 text-white bg-gradient-to-br ${toneMap[tone]}`} data-testid={`payout-${label.toLowerCase().replace(/\W+/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-display font-bold tabular-nums mt-1">{formatINR(amount)}</p>
      <p className="text-[10px] opacity-80 mt-1">{count || 0} invoice{count === 1 ? '' : 's'}</p>
    </div>
  );
}
